import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useBranches } from "@/hooks/useBranches";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, Plus, Loader2, Package, ArrowRight, CheckCircle2, Info } from "lucide-react";

export default function LocationsPage() {
  const { branches, loading, error, fetchBranches, canAddBranch, atLimit, plan, branchLimit } = useBranches();
  const { openPaywall } = usePaywallModal();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleAddBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (plan === "free") {
      openPaywall("location_limit");
      setAddOpen(false);
      return;
    }
    if (atLimit) {
      openPaywall("location_limit");
      setAddOpen(false);
      return;
    }
    setSubmitting(true);
    const { data, error: rpcError } = await supabase.rpc("create_branch", { p_name: newName.trim() || undefined });
    setSubmitting(false);
    if (rpcError) {
      const msg = rpcError.message || "Could not create location.";
      if (msg.includes("Upgrade to Pro") || msg.includes("Pro Solo allows 1") || msg.includes("reached the limit")) {
        openPaywall("location_limit");
        setAddOpen(false);
      } else {
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
      return;
    }
    toast({ title: "Location added", description: (data as { name?: string })?.name ? `"${(data as { name: string }).name}" created.` : "New location created." });
    setNewName("");
    setAddOpen(false);
    fetchBranches();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Locations</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {plan === "free"
              ? "Upgrade to Pro Solo or Pro Growth to manage multiple locations."
              : plan === "pro_growth"
                ? `Track stock per shop or branch — up to ${branchLimit} locations. Know what you have where.`
                : `You can have up to ${branchLimit} location${branchLimit === 1 ? "" : "s"} on your current plan.`}
          </p>
        </div>
        {plan !== "free" && (
          <Button
            onClick={() => {
              if (atLimit) {
                openPaywall("location_limit");
                return;
              }
              setAddOpen(true);
            }}
            disabled={atLimit}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add location
          </Button>
        )}
      </div>

      {error && (
        <p className="text-destructive text-sm">{error}</p>
      )}

      {plan !== "free" && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Why locations?
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              Track stock separately per shop or branch so you know what you have where — and move stock between locations when needed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <ul className="list-disc list-inside space-y-1">
              <li>See which location has stock and which is low</li>
              <li>Add products to a specific branch when receiving delivery there</li>
              <li>Transfer stock between branches (e.g. City Mall → Industrial) in one click</li>
              <li>Filter Inventory by location so each branch can work with its own view</li>
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Your locations
          </CardTitle>
          <CardDescription className="text-sm">
            {plan === "free"
              ? "Pro plans let you add branches so you can track stock per location."
              : branches.length === 0
                ? "Add your first location below, then assign and move stock in Inventory."
                : "Add locations here. Use Inventory to assign products and transfer stock between them."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {branches.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {plan === "free"
                ? "No locations yet. Upgrade to add your first location."
                : "No locations yet. Click “Add location” above to create one."}
            </p>
          ) : (
            <ul className="space-y-2">
              {branches.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50"
                >
                  <span className="font-medium">{b.name}</span>
                  <span className="text-muted-foreground text-xs">Branch</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {plan !== "free" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">How to use locations</CardTitle>
            <CardDescription className="text-sm">
              Three steps to get value from multi-location inventory.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">1</span>
              <div>
                <p className="font-medium text-sm">Add your branches here</p>
                <p className="text-sm text-muted-foreground">Click “Add location” and name each shop or branch (e.g. City Mall, Industrial). You can have up to {branchLimit} on your plan.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">2</span>
              <div>
                <p className="font-medium text-sm">Assign products to a location in Inventory</p>
                <p className="text-sm text-muted-foreground">In Inventory, add or edit products and choose which location they belong to. Use the location filter to see one branch at a time.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">3</span>
              <div>
                <p className="font-medium text-sm">Transfer stock when you move it</p>
                <p className="text-sm text-muted-foreground">When you move stock from one branch to another, use “Transfer” on that product in Inventory to keep counts accurate at both locations.</p>
              </div>
            </div>
            <Button asChild variant="outline" className="mt-2">
              <Link to="/inventory" className="inline-flex items-center gap-2">
                <Package className="h-4 w-4" />
                Go to Inventory
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {plan === "pro_growth" && (
        <Card className="border-muted">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              How Pro Growth works
            </CardTitle>
            <CardDescription className="text-sm">
              Your plan includes everything below. Locations are the main extra you get over Pro Solo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <ul className="space-y-1.5 list-none">
              <li className="flex gap-2">
                <span className="text-primary font-medium shrink-0">Locations:</span>
                Up to 3 branches. Add them here, then assign and transfer stock in Inventory. The dashboard shows each location’s item count and critical stock.
              </li>
              <li className="flex gap-2">
                <span className="text-primary font-medium shrink-0">Team:</span>
                Up to 10 seats (Pro Solo has 5). Invite staff in Team and set roles; they get access to Live Stock, Manual Entry, and Expenses as you allow.
              </li>
              <li className="flex gap-2">
                <span className="text-primary font-medium shrink-0">BI &amp; reports:</span>
                Same as Pro Solo — full dashboard, AI insights, Reports, Master Ledger, and Manual Entry. Sales and expenses are at business level; only inventory is split by location.
              </li>
            </ul>
            <p className="pt-1 text-xs">
              Your subscription is managed in <Link to="/settings" className="text-primary hover:underline">Settings → Subscription &amp; Billing</Link>.
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add location</DialogTitle>
            <DialogDescription>
              Give this branch a clear name (e.g. City Mall, Industrial). You’ll use it in Inventory to filter and assign stock.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddBranch} className="space-y-4">
            <div>
              <Label htmlFor="branch-name">Location name</Label>
              <Input
                id="branch-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. City Mall, Industrial"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
